import React, { useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { NavLink } from 'react-router-dom';
import { Spring } from 'react-spring/renderprops';
import './styles.css';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicControlGroup from '../../../../components/BasicControlGroup';
import LinkButton from '../../../../components/LinkButton';
import PanelContainer from '../../../../components/PanelContainer';
import {ANIMATION_TIMEOUT_INOUT} from '../../../../constants/animation';
import LinkBuilder from '../../../../helpers/link-builder';
import {linkedInShareUrl} from '../../../../helpers/share';
import StringHelpers from '../../../../helpers/string-helpers';

const ProcessItem = props => {
  const { allowAssign, data, actions, isRestricted } = props;
  const {
    applicants,
    ProcessID,
    Type,
    Descriptions,
    StartDate,
    PositionTitle,
    AssignedFirmName,
    AssignedFirmID,
    Closed,
    AllowPush,
    AllowEdit,
    AllowSetupEmail,
    Approved,
    BlockLinkedInSharing,
  } = data;

  const {
    handlePush,
    handleRefresh,
    handleClose,
    handleReopen,
    handleAssign,
    handleRemoveAssign,
    handleDelete,
    handleViewStatistics,
    handleViewApplicants,
  } = actions;

  const allowPush = parseInt(AllowPush) === 1;
  const allowEdit = parseInt(AllowEdit) === 1;
  const allowSetupEmail = parseInt(AllowSetupEmail) === 1;
  const closed = parseInt(Closed) === 1;

  const text = useMemo(
    () =>
      StringHelpers.unescape(
        StringHelpers.trimString(Descriptions, 30, '... ')
      ),
    [Descriptions]
  );

  const onPush = () => {
    handlePush(data);
  };

  const onRefresh = () => {
    handleRefresh(data);
  };

  const onClose = () => {
    handleClose(data);
  };

  const onReopen = () => {
    handleReopen(data);
  };

  const onAssign = () => {
    handleAssign(data);
  };

  const onRemoveAssign = () => {
    handleRemoveAssign(data);
  };

  const onDelete = () => {
    handleDelete(data);
  };

  const onViewStatistics = () => {
    handleViewStatistics(data);
  };

  const onViewApplicants = () => {
    handleViewApplicants(data);
  };

  const handleShare = () =>
    linkedInShareUrl(
      window.location.origin +
        LinkBuilder.getProcessPublicLink(
          data.Id,
          Type === 'Job' ? 'process' : 'project'
        )
    );

  const link =
    Type === 'Job'
      ? LinkBuilder.getJobProfileLink(ProcessID)
      : LinkBuilder.getProjectProfileLink(ProcessID);

  return (
    <Spring
      from={{ opacity: 0 }}
      to={{ opacity: 1 }}
      config={{ duration: ANIMATION_TIMEOUT_INOUT }}
    >
      {props => (
        <PanelContainer
          styles={(props, { paddingTop: '30px', marginBottom: '10px' })}
        >
          <div className="process-item">
            {allowEdit && (
              <div className="process-item__controls">
                {closed ? (
                  <BasicControlGroup>
                    <LinkButton text="Reopen" controlFunc={onReopen} />
                    <LinkButton text="Delete" controlFunc={onDelete} />
                  </BasicControlGroup>
                ) : (
                  <BasicControlGroup>
                    {BlockLinkedInSharing === 'No' && Approved === 'Yes' && (
                      <LinkButton
                        text="Share on LinkedIn"
                        controlFunc={handleShare}
                      />
                    )}
                    {Approved === 'Yes' && (
                      <LinkButton
                        text={`Refresh ${Type}`}
                        controlFunc={onRefresh}
                      />
                    )}
                    {allowPush && (
                      <LinkButton text={`Push ${Type}`} controlFunc={onPush} />
                    )}
                    {allowEdit && (
                      <LinkButton text="Edit" to={`${link}/edit`} />
                    )}
                    {allowSetupEmail && (
                      <LinkButton
                        text="Setup Email"
                        to={`${link}/email-setup`}
                      />
                    )}
                    <LinkButton
                      text={`Close ${Type === 'Job' ? 'Process' : 'Project'}`}
                      controlFunc={onClose}
                    />
                  </BasicControlGroup>
                )}
              </div>
            )}

            <div className="process-item__content">
              <div className="process-item__highlights">
                <div className="process-item__highlight">
                  <div className="process-item__block">
                    <div className="process-item__name">Applications</div>
                    <div className="process-item__value">
                      <NumberFormat
                        thousandSeparator
                        value={applicants}
                        displayType={'text'}
                      />
                    </div>
                  </div>
                </div>
                <div className="process-item__highlight">
                  <div className="process-item__block">
                    <div className="process-item__name">Start Date</div>
                    <div className="process-item__value">{StartDate}</div>
                  </div>
                </div>
                <div className="process-item__highlight">
                  <div className="process-item__block">
                    <div className="process-item__name">Position</div>
                    <div className="process-item__value">{PositionTitle}</div>
                  </div>
                </div>
              </div>
              <div className="process-item__description">
                {text}
                <NavLink className="read-more-button" to={link}>
                  Read more
                </NavLink>
              </div>
            </div>
            {AssignedFirmID && allowAssign && !closed && (
              <div className="process-item__assigned">
                <span className="process-item__firmname">
                  Assigned to: <strong>{AssignedFirmName} </strong>
                </span>
                <LinkButton text="Remove Assign" controlFunc={onRemoveAssign} />
              </div>
            )}

            <div className="proccess-item__footer">
              <BasicButtonGroup>
                {applicants > 0 && !(isRestricted && closed) && (
                  <BasicButton
                    btnWidth={200}
                    btnHeight={35}
                    btnFontWeight={600}
                    btnText="View Applicants"
                    btnArrow={false}
                    controlFunc={onViewApplicants}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                  />
                )}

                {applicants > 0 && (
                  <BasicButton
                    btnWidth={200}
                    btnHeight={35}
                    btnFontWeight={600}
                    btnText="View Statistics"
                    btnArrow={false}
                    controlFunc={onViewStatistics}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                  />
                )}

                {!AssignedFirmID && allowAssign && !closed && (
                  <BasicButton
                    btnWidth={200}
                    btnHeight={35}
                    btnFontWeight={600}
                    btnText="Assign Search"
                    btnArrow={false}
                    controlFunc={onAssign}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                  />
                )}
              </BasicButtonGroup>
            </div>
          </div>
        </PanelContainer>
      )}
    </Spring>
  );
};

export default ProcessItem;
